import { useSyncExternalStore } from 'react';

import type { PrepaidContractListRow, PrepaidRunEntity, PrepaidStatus } from '@dcc-erp/shared';
import { fmtDate, thaiDate } from '@dcc-erp/shared';

// Resolved per-company prepaid-asset control account (PREPAID_ASSET in
// account-mappings.default.ts). Per-company resolution can replace this constant later.
export const PREPAID_ASSET_CODE = '10190001';

export type StatusKey = 'pending' | 'active' | 'completed';
export type CatCode = 'INS' | 'RENT' | 'SW' | 'MAINT' | 'AD' | 'FEE' | 'OTHER';

// ============================================================
// P9 §9.6 — per-user พ.ศ. / ค.ศ. date-display toggle. A tiny module-level store
// (localStorage-backed) shared across every prepaid surface via useSyncExternalStore,
// so the header toggle reformats the register/schedule/drawer dates without prop
// threading. `df` is the active date formatter (thaiDate for BE, fmtDate for CE).
// ============================================================
export type DateMode = 'CE' | 'BE';
function readStoredDateMode(): DateMode {
  try {
    return localStorage.getItem('prepaid.dateMode') === 'BE' ? 'BE' : 'CE';
  } catch {
    return 'CE';
  }
}
let dateModeState: DateMode = readStoredDateMode();
const dateModeListeners = new Set<() => void>();
export function setDateMode(m: DateMode): void {
  dateModeState = m;
  try {
    localStorage.setItem('prepaid.dateMode', m);
  } catch {
    // ignore (private mode / SSR)
  }
  dateModeListeners.forEach((l) => l());
}
export function useDateMode(): {
  mode: DateMode;
  setMode: (m: DateMode) => void;
  df: typeof fmtDate;
} {
  const mode = useSyncExternalStore(
    (cb) => {
      dateModeListeners.add(cb);
      return () => dateModeListeners.delete(cb);
    },
    () => dateModeState,
    (): DateMode => 'CE',
  );
  const df = mode === 'BE' ? thaiDate : fmtDate;
  return { mode, setMode: setDateMode, df };
}

export interface PPCat {
  code: CatCode;
  label: string;
  acct: string;
  tone: string;
}

export interface PPItem {
  id: string;
  apiId?: string; // PrepaidContract uuid (present for API rows, absent for mock PP_DATA)
  apiStatus?: PrepaidStatus; // real lifecycle status (ACTIVE/SUSPENDED/FULLY_AMORTIZED/TERMINATED)
  requiresEvidence?: boolean; // company opted-in evidence gate, pre-first-run (§9.5)
  missingEvidenceCount?: number; // >0 → amber "ขาดเอกสาร" register badge
  acquired: string;
  vendor: string;
  cat: CatCode;
  desc: string;
  total: number;
  period: number;
  used: number;
  remain: number;
  monthly: number;
  start: string;
  end: string;
  payRef: string;
  status: StatusKey;
}

// Phase 12: prepaid expense codes renumbered 52xx → 62xx (EXPENSE moved
// out of the 5xxx range).
export const PP_CATS: readonly PPCat[] = [
  { code: 'INS', label: 'เบี้ยประกัน', acct: '6230 ค่าใช้จ่ายดำเนินงาน', tone: '#1a56a6' },
  { code: 'RENT', label: 'ค่าเช่าจ่ายล่วงหน้า', acct: '6240 ค่าเช่า', tone: '#7030A0' },
  { code: 'SW', label: 'License ซอฟต์แวร์', acct: '6260 ค่าใช้จ่ายไอที', tone: '#117a3d' },
  {
    code: 'MAINT',
    label: 'สัญญาบำรุงรักษา',
    acct: '6270 ค่าซ่อมแซมและบำรุงรักษา',
    tone: '#b54708',
  },
  {
    code: 'AD',
    label: 'ค่าโฆษณาจ่ายล่วงหน้า',
    acct: '6280 ค่าโฆษณาและส่งเสริมการขาย',
    tone: '#b42318',
  },
  { code: 'FEE', label: 'ค่าธรรมเนียม / ใบอนุญาต', acct: '6290 ค่าธรรมเนียม', tone: '#a16207' },
  { code: 'OTHER', label: 'อื่นๆ', acct: '6299 ค่าใช้จ่ายอื่น', tone: '#52525b' },
];

export const PP_FALLBACK: PPCat = PP_CATS[6]!;

export function getCat(code: CatCode): PPCat {
  return PP_CATS.find((c) => c.code === code) ?? PP_FALLBACK;
}

// Format a YYYYMM period string to a short Thai month + year label.
// e.g. '202605' → 'พ.ค. 2026'
export function fmtPeriod(period: string): string {
  const yr = parseInt(period.slice(0, 4), 10);
  const mo = parseInt(period.slice(4, 6), 10) - 1;
  const d = new Date(yr, mo, 1);
  return d.toLocaleDateString('th-TH', { month: 'short', year: 'numeric' });
}

// Map API rows into the prototype's PPItem shape. Used inside PrepaidPage when
// the backend returns contracts — the mock PP_DATA seeds the visual fidelity
// when the API is empty (e.g. dev DBs without prior seed). Categories are
// derived heuristically from the expense account code.
export function apiRowToPPItem(r: PrepaidContractListRow): PPItem {
  // Phase 12: prepaid expense codes renumbered 52xx → 62xx (EXPENSE moved
  // out of 5xxx range). The real COA is 8-digit (e.g. 62300001), so match on
  // the 4-digit category group prefix rather than the whole code.
  const acct = r.expenseAccountCode.slice(0, 4);
  const cat: CatCode =
    acct === '6230'
      ? 'INS'
      : acct === '6240'
        ? 'RENT'
        : acct === '6260'
          ? 'SW'
          : acct === '6270'
            ? 'MAINT'
            : acct === '6280'
              ? 'AD'
              : acct === '6290'
                ? 'FEE'
                : 'OTHER';
  const total = Number(r.totalAmount);
  const monthly = Number(r.monthlyAmount);
  const startIso = (r.startDate instanceof Date ? r.startDate : new Date(r.startDate))
    .toISOString()
    .slice(0, 10);
  const endIso = (r.endDate instanceof Date ? r.endDate : new Date(r.endDate))
    .toISOString()
    .slice(0, 10);
  return {
    id: r.code,
    apiId: r.id,
    apiStatus: r.status,
    requiresEvidence: r.requiresEvidence ?? false,
    missingEvidenceCount: r.missingEvidenceCount ?? 0,
    acquired: startIso,
    vendor: r.vendorName ?? '—',
    cat,
    desc: r.description,
    total,
    period: r.monthsTotal,
    used: r.monthsAmortized,
    remain: Math.max(0, r.monthsTotal - r.monthsAmortized),
    monthly,
    start: startIso,
    end: endIso,
    payRef: r.code,
    status:
      r.status === 'FULLY_AMORTIZED' ? 'completed' : r.monthsAmortized === 0 ? 'pending' : 'active',
  };
}

// P9 — days from today (local midnight) until a contract's end date, or null if
// unparseable. Negative = already past. Shared by the expiry alerts strip and
// the "ใกล้ครบกำหนด" saved-view preset filter (§3.7 saved views).
export function prepaidDaysToEnd(endIso: string): number | null {
  const [ey, em, ed] = endIso.split('-').map(Number);
  if (!ey || !em || !ed) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(ey, em - 1, ed);
  end.setHours(0, 0, 0, 0);
  return Math.ceil((end.getTime() - today.getTime()) / 86_400_000);
}

// P9 §3.6/§3.7 — renewal-alert + "ใกล้ครบกำหนด" preset look-ahead window (days).
export const EXPIRY_THRESHOLD_DAYS = 90;

// P9 — a still-live (not completed/terminated/suspended, with balance left)
// contract whose schedule ends within `withinDays` days.
export function isExpiringSoon(r: PPItem, withinDays = EXPIRY_THRESHOLD_DAYS): boolean {
  if (r.status === 'completed') return false;
  if (r.apiStatus === 'TERMINATED' || r.apiStatus === 'SUSPENDED') return false;
  if (r.remain <= 0) return false;
  const d = prepaidDaysToEnd(r.end);
  return d !== null && d <= withinDays;
}

// App-default register date range — also the deterministic range a preset/view
// resets to (so applying a view never leaves a stale custom range, §3.7).
export const PREPAID_DATE_FROM_DEFAULT = '2026-01-01';
export const PREPAID_DATE_TO_DEFAULT = '2026-12-31';

export const todayISO = () => new Date().toISOString().slice(0, 10);

// 'YYYY-MM-DD' + 1 calendar day (local) — a renewal starts after the source ends.
export function dayAfterIso(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const next = new Date(y, m - 1, d + 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
}

export function runRangeLabel(run: PrepaidRunEntity): string {
  return run.fromPeriod === run.toPeriod
    ? fmtPeriod(run.fromPeriod)
    : `${fmtPeriod(run.fromPeriod)} – ${fmtPeriod(run.toPeriod)}`;
}
